import { db } from "@/firebase";
import { collection, doc, getDocs, deleteDoc, updateDoc, query, where } from "firebase/firestore";

export const deleteOrganizationComplete = async (organizationId: string) => {
  if (!organizationId) return;

  // 1. Get all members from /organizations/{organizationId}/access
  let memberIds: string[] = [];
  try {
    const accessColRef = collection(db, "organizations", organizationId, "access");
    const accessSnap = await getDocs(accessColRef);
    memberIds = accessSnap.docs.map(doc => doc.id);
  } catch (err) {
    console.error("Error reading organization access list:", err);
  }

  // 2. Delete /users/{userId}/access/{organizationId} for each member
  for (const userId of memberIds) {
    try {
      const userAccessDocRef = doc(db, "users", userId, "access", organizationId);
      await deleteDoc(userAccessDocRef);
    } catch (err) {
      console.error(`Error deleting user access for user ${userId}:`, err);
    }
  }

  // 3. Delete aliases referencing organizationId
  try {
    const aliasQuery = query(collection(db, "aliases"), where("organizationId", "==", organizationId));
    const aliasSnap = await getDocs(aliasQuery);
    for (const aliasDoc of aliasSnap.docs) {
      try {
        await deleteDoc(doc(db, "aliases", aliasDoc.id));
      } catch (err) {
        console.error(`Error deleting alias ${aliasDoc.id}:`, err);
      }
    }
  } catch (err) {
    console.error("Error querying aliases:", err);
  }

  // 4. Delete invitations referencing orgId
  try {
    const inviteQuery = query(collection(db, "invitations"), where("orgId", "==", organizationId));
    const inviteSnap = await getDocs(inviteQuery);
    for (const inviteDoc of inviteSnap.docs) {
      try {
        await deleteDoc(doc(db, "invitations", inviteDoc.id));
      } catch (err) {
        console.error(`Error deleting invitation ${inviteDoc.id}:`, err);
      }
    }
  } catch (err) {
    console.error("Error querying invitations:", err);
  }

  // 5. Delete loyalty accounts referencing orgId (including their transactions subcollection)
  try {
    const loyaltyQuery = query(collection(db, "loyaltyAccounts"), where("orgId", "==", organizationId));
    const loyaltySnap = await getDocs(loyaltyQuery);
    for (const loyaltyDoc of loyaltySnap.docs) {
      try {
        // Fetch and delete transactions subcollection
        try {
          const transactionsSnap = await getDocs(collection(db, "loyaltyAccounts", loyaltyDoc.id, "transactions"));
          for (const transDoc of transactionsSnap.docs) {
            await deleteDoc(doc(db, "loyaltyAccounts", loyaltyDoc.id, "transactions", transDoc.id));
          }
        } catch (err) {
          console.error(`Error deleting loyalty transactions for account ${loyaltyDoc.id}:`, err);
        }
        // Delete the loyalty account itself
        await deleteDoc(doc(db, "loyaltyAccounts", loyaltyDoc.id));
      } catch (err) {
        console.error(`Error deleting loyalty account ${loyaltyDoc.id}:`, err);
      }
    }
  } catch (err) {
    console.error("Error querying loyalty accounts:", err);
  }

  // 6. Delete or update drivers referencing organizationId
  try {
    const driversQuery = query(collection(db, "drivers"), where("organizations", "array-contains", organizationId));
    const driversSnap = await getDocs(driversQuery);
    for (const driverDoc of driversSnap.docs) {
      try {
        const driverData = driverDoc.data();
        const orgs = driverData.organizations || [];
        const updatedOrgs = orgs.filter((id: string) => id !== organizationId);
        if (updatedOrgs.length === 0) {
          // Driver only belonged to this organization, delete completely
          await deleteDoc(doc(db, "drivers", driverDoc.id));
        } else {
          // Driver belongs to other organizations, just update list
          await updateDoc(doc(db, "drivers", driverDoc.id), {
            organizations: updatedOrgs
          });
        }
      } catch (err) {
        console.error(`Error processing driver ${driverDoc.id}:`, err);
      }
    }
  } catch (err) {
    console.error("Error querying drivers:", err);
  }

  // 7. Delete all subcollections of the organization itself
  const subcollections = [
    "menus",
    "content",
    "orders",
    "archivedOrders",
    "completedOrders",
    "paidOrders",
    "avatars",
    "faqs",
    "branches",
    "access",
    "websites",
    "quotes",
    "categories",
    "tables",
    "surveys",
    "customers",
    "printJobs"
  ];

  for (const subcol of subcollections) {
    try {
      const subcolRef = collection(db, "organizations", organizationId, subcol);
      const subcolSnap = await getDocs(subcolRef);
      for (const subDoc of subcolSnap.docs) {
        // Special case: content has a nested subcollection settings
        if (subcol === "content") {
          try {
            const settingsSnap = await getDocs(collection(db, "organizations", organizationId, "content", subDoc.id, "settings"));
            for (const settingDoc of settingsSnap.docs) {
              await deleteDoc(doc(db, "organizations", organizationId, "content", subDoc.id, "settings", settingDoc.id));
            }
          } catch (err) {
            console.error(`Error deleting content settings for content ${subDoc.id}:`, err);
          }
        }
        await deleteDoc(doc(db, "organizations", organizationId, subcol, subDoc.id));
      }
    } catch (err) {
      console.error(`Error deleting subcollection ${subcol} for organization ${organizationId}:`, err);
    }
  }

  // 8. Finally, delete the organization document itself
  try {
    await deleteDoc(doc(db, "organizations", organizationId));
  } catch (err) {
    console.error(`Error deleting organization document ${organizationId} itself:`, err);
    throw err;
  }
};
